import { z } from 'zod';

/**
 * Enum helper: canonical UPPER_SNAKE codes (what the DB, REST and MCP use), human labels (what the
 * UI shows), and a tolerant parser that accepts codes, labels and common aliases in any case
 * ("HR interview", "hr_interview", "Proposed No-go", "per day").
 */
export interface EnumDef<T extends string> {
  readonly codes: readonly T[];
  readonly labels: Readonly<Record<T, string>>;
  readonly schema: z.ZodEnum<[T, ...T[]]>;
  /** Accepts codes, labels or aliases (case-insensitive) and returns the canonical code. */
  readonly input: z.ZodEffects<z.ZodString, T, string>;
  label(code: T): string;
  parse(value: unknown): T;
  tryParse(value: unknown): T | undefined;
  is(value: unknown): value is T;
}

export function normalizeToken(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function defineEnum<const T extends string>(
  codes: readonly T[],
  labels: Readonly<Record<T, string>>,
  aliases: Readonly<Record<string, T>> = {},
): EnumDef<T> {
  const lookup = new Map<string, T>();
  for (const code of codes) {
    lookup.set(normalizeToken(code), code);
    lookup.set(normalizeToken(labels[code]), code);
  }
  for (const [alias, code] of Object.entries(aliases)) lookup.set(normalizeToken(alias), code);

  const tryParse = (value: unknown): T | undefined => {
    if (typeof value !== 'string') return undefined;
    return lookup.get(normalizeToken(value));
  };
  const parse = (value: unknown): T => {
    const parsed = tryParse(value);
    if (parsed === undefined) {
      throw new Error(`Invalid value "${String(value)}". Expected one of: ${codes.join(', ')}`);
    }
    return parsed;
  };
  const schema = z.enum(codes as unknown as [T, ...T[]]);
  const input = z.string().transform((v, ctx) => {
    const parsed = tryParse(v);
    if (parsed === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid value "${v}". Expected one of: ${codes.join(', ')}`,
      });
      return z.NEVER;
    }
    return parsed;
  });
  return {
    codes,
    labels,
    schema,
    input,
    label: (code) => labels[code] ?? code,
    parse,
    tryParse,
    is: (value): value is T => typeof value === 'string' && (codes as readonly string[]).includes(value),
  };
}

// ---------------------------------------------------------------------------

export const PartnerType = defineEnum(['INTERMEDIARY', 'SUPPLIER', 'FREELANCER'] as const, {
  INTERMEDIARY: 'Intermediary',
  SUPPLIER: 'Supplier',
  FREELANCER: 'Freelancer',
});
export type PartnerType = (typeof PartnerType.codes)[number];

export const AgreementStatus = defineEnum(
  ['NONE', 'NDA_SENT', 'NDA_SIGNED', 'FRAMEWORK_SIGNED'] as const,
  {
    NONE: 'None',
    NDA_SENT: 'NDA sent',
    NDA_SIGNED: 'NDA signed',
    FRAMEWORK_SIGNED: 'Framework signed',
  },
  { framework: 'FRAMEWORK_SIGNED', 'framework agreement signed': 'FRAMEWORK_SIGNED' },
);
export type AgreementStatus = (typeof AgreementStatus.codes)[number];

export const PositionStage = defineEnum(
  ['NEW', 'SOURCING', 'SUBMITTED', 'INTERVIEWING', 'OFFER', 'FILLED', 'LOST', 'DROPPED'] as const,
  {
    NEW: 'New',
    SOURCING: 'Sourcing',
    SUBMITTED: 'Submitted',
    INTERVIEWING: 'Interviewing',
    OFFER: 'Offer',
    FILLED: 'Filled',
    LOST: 'Lost',
    DROPPED: 'Dropped',
  },
  { interview: 'INTERVIEWING', interviews: 'INTERVIEWING', placed: 'FILLED', closed: 'LOST' },
);
export type PositionStage = (typeof PositionStage.codes)[number];

export const WorkModel = defineEnum(
  ['REMOTE', 'HYBRID', 'ON_SITE'] as const,
  { REMOTE: 'Remote', HYBRID: 'Hybrid', ON_SITE: 'On-site' },
  { onsite: 'ON_SITE', 'on site': 'ON_SITE', 'vor ort': 'ON_SITE', office: 'ON_SITE' },
);
export type WorkModel = (typeof WorkModel.codes)[number];

export const RateUnit = defineEnum(
  ['HOUR', 'DAY'] as const,
  { HOUR: 'per hour', DAY: 'per day' },
  {
    h: 'HOUR',
    hr: 'HOUR',
    hourly: 'HOUR',
    'eur/h': 'HOUR',
    'eur per hour': 'HOUR',
    stunde: 'HOUR',
    d: 'DAY',
    daily: 'DAY',
    'eur/day': 'DAY',
    'eur/d': 'DAY',
    'eur per day': 'DAY',
    'person day': 'DAY',
    tag: 'DAY',
  },
);
export type RateUnit = (typeof RateUnit.codes)[number];

export const GoDecision = defineEnum(
  ['PENDING', 'PROPOSED_GO', 'PROPOSED_NO_GO', 'GO', 'NO_GO', 'CHECK_FIRST'] as const,
  {
    PENDING: 'Pending',
    PROPOSED_GO: 'Proposed Go',
    PROPOSED_NO_GO: 'Proposed No-go',
    GO: 'Go',
    NO_GO: 'No-go',
    CHECK_FIRST: 'Check first',
  },
  { 'proposed nogo': 'PROPOSED_NO_GO', nogo: 'NO_GO', check: 'CHECK_FIRST' },
);
export type GoDecision = (typeof GoDecision.codes)[number];

export const PortalStatus = defineEnum(['OPEN', 'CLOSED'] as const, { OPEN: 'Open', CLOSED: 'Closed' });
export type PortalStatus = (typeof PortalStatus.codes)[number];

export const PortalStatusDetail = defineEnum(
  ['OPEN', 'ENOUGH_CVS', 'CLOSED', 'NOT_FOUND'] as const,
  { OPEN: 'Open', ENOUGH_CVS: 'Enough CVs', CLOSED: 'Closed', NOT_FOUND: 'Not found' },
  { 'enough cv': 'ENOUGH_CVS', enough: 'ENOUGH_CVS', missing: 'NOT_FOUND', notfound: 'NOT_FOUND' },
);
export type PortalStatusDetail = (typeof PortalStatusDetail.codes)[number];

/** Maps the portal detail to the coarse Open/Closed status. "Enough CVs" counts as closed for new CVs. */
export function portalStatusFromDetail(detail: PortalStatusDetail): PortalStatus {
  return detail === 'OPEN' ? 'OPEN' : 'CLOSED';
}

export const ProjectType = defineEnum(['OUTSTAFF', 'OUTSOURCE', 'MSP'] as const, {
  OUTSTAFF: 'Outstaff',
  OUTSOURCE: 'Outsource',
  MSP: 'MSP',
});
export type ProjectType = (typeof ProjectType.codes)[number];

export const CandidateSource = defineEnum(
  ['LINKEDIN', 'REFERRAL', 'JOB_BOARD', 'OUTBOUND', 'SUPPLIER'] as const,
  {
    LINKEDIN: 'LinkedIn',
    REFERRAL: 'Referral',
    JOB_BOARD: 'Job Board',
    OUTBOUND: 'Outbound',
    SUPPLIER: 'Supplier',
  },
  { jobboard: 'JOB_BOARD', partner: 'SUPPLIER' },
);
export type CandidateSource = (typeof CandidateSource.codes)[number];

export const PoolStatus = defineEnum(
  ['ACTIVE', 'ON_HOLD', 'PLACED', 'INACTIVE'] as const,
  { ACTIVE: 'Active', ON_HOLD: 'On hold', PLACED: 'Placed', INACTIVE: 'Inactive' },
  { hold: 'ON_HOLD', onhold: 'ON_HOLD' },
);
export type PoolStatus = (typeof PoolStatus.codes)[number];

export const SubmissionStatus = defineEnum(
  [
    'SCREENING',
    'SUBMITTED',
    'HR_INTERVIEW',
    'CLIENT_INTERVIEW',
    'OFFER',
    'PLACED',
    'REJECTED',
    'WITHDRAWN',
  ] as const,
  {
    SCREENING: 'Screening',
    SUBMITTED: 'Submitted',
    HR_INTERVIEW: 'HR interview',
    CLIENT_INTERVIEW: 'Client interview',
    OFFER: 'Offer',
    PLACED: 'Placed',
    REJECTED: 'Rejected',
    WITHDRAWN: 'Withdrawn',
  },
  {
    hr: 'HR_INTERVIEW',
    'hr-interview': 'HR_INTERVIEW',
    client: 'CLIENT_INTERVIEW',
    interview: 'CLIENT_INTERVIEW',
  },
);
export type SubmissionStatus = (typeof SubmissionStatus.codes)[number];

export const OutcomeReason = defineEnum(
  [
    'SKILLS',
    'RATE',
    'LANGUAGE',
    'AVAILABILITY',
    'CLIENT_CLOSED_ROLE',
    'CANDIDATE_WITHDREW',
    'PLACED',
  ] as const,
  {
    SKILLS: 'Skills',
    RATE: 'Rate',
    LANGUAGE: 'Language',
    AVAILABILITY: 'Availability',
    CLIENT_CLOSED_ROLE: 'Client closed the role',
    CANDIDATE_WITHDREW: 'Candidate withdrew',
    PLACED: 'Placed',
  },
  {
    'client closed role': 'CLIENT_CLOSED_ROLE',
    'role closed': 'CLIENT_CLOSED_ROLE',
    'client closed': 'CLIENT_CLOSED_ROLE',
    withdrew: 'CANDIDATE_WITHDREW',
    withdrawn: 'CANDIDATE_WITHDREW',
    skill: 'SKILLS',
    price: 'RATE',
    languages: 'LANGUAGE',
  },
);
export type OutcomeReason = (typeof OutcomeReason.codes)[number];

export const WaitingOn = defineEnum(
  ['CLIENT', 'INTERMEDIARY', 'SUPPLIER', 'CANDIDATE', 'US'] as const,
  { CLIENT: 'Client', INTERMEDIARY: 'Intermediary', SUPPLIER: 'Supplier', CANDIDATE: 'Candidate', US: 'Us' },
  {
    we: 'US',
    'data-claw': 'US',
    'data claw': 'US',
    internal: 'US',
    partner: 'SUPPLIER',
    portal: 'INTERMEDIARY',
  },
);
export type WaitingOn = (typeof WaitingOn.codes)[number];

export const MarginSource = defineEnum(
  ['SUBMISSION', 'POSITION', 'GLOBAL'] as const,
  { SUBMISSION: 'this submission', POSITION: 'position', GLOBAL: 'global' },
  { settings: 'GLOBAL', default: 'GLOBAL' },
);
export type MarginSource = (typeof MarginSource.codes)[number];

export const EntityType = defineEnum(
  [
    'PARTNER',
    'POSITION',
    'CANDIDATE',
    'SUBMISSION',
    'JD_VERSION',
    'PROFILE_VERSION',
    'FILE',
    'USER',
    'SETTINGS',
    'MESSAGE_TEMPLATE',
    'SAVED_VIEW',
    'API_TOKEN',
    'OPTION_ITEM',
    'IMPORT',
  ] as const,
  {
    PARTNER: 'Partner',
    POSITION: 'Position',
    CANDIDATE: 'Candidate',
    SUBMISSION: 'Submission',
    JD_VERSION: 'JD version',
    PROFILE_VERSION: 'Profile version',
    FILE: 'File',
    USER: 'User',
    SETTINGS: 'Settings',
    MESSAGE_TEMPLATE: 'Message template',
    SAVED_VIEW: 'Saved view',
    API_TOKEN: 'API token',
    OPTION_ITEM: 'Option item',
    IMPORT: 'Import',
  },
  {
    partners: 'PARTNER',
    positions: 'POSITION',
    candidates: 'CANDIDATE',
    submissions: 'SUBMISSION',
    jd: 'JD_VERSION',
    'jd version': 'JD_VERSION',
    'job description': 'JD_VERSION',
    profile: 'PROFILE_VERSION',
    files: 'FILE',
    activity: 'IMPORT',
  },
);
export type EntityType = (typeof EntityType.codes)[number];

/** Entity types that can own activity entries, follow-ups and files. */
export const DomainEntityType = defineEnum(
  ['PARTNER', 'POSITION', 'CANDIDATE', 'SUBMISSION'] as const,
  { PARTNER: 'Partner', POSITION: 'Position', CANDIDATE: 'Candidate', SUBMISSION: 'Submission' },
  { partners: 'PARTNER', positions: 'POSITION', candidates: 'CANDIDATE', submissions: 'SUBMISSION' },
);
export type DomainEntityType = (typeof DomainEntityType.codes)[number];

export const ActivityKind = defineEnum(['MANUAL', 'AUTO'] as const, { MANUAL: 'Manual', AUTO: 'Automatic' });
export type ActivityKind = (typeof ActivityKind.codes)[number];

export const DocLang = defineEnum(
  ['EN', 'DE'] as const,
  { EN: 'English', DE: 'German' },
  { english: 'EN', german: 'DE', deutsch: 'DE', englisch: 'EN' },
);
export type DocLang = (typeof DocLang.codes)[number];

export const FileKind = defineEnum(
  [
    'CV_ORIGINAL',
    'PROFILE_PDF',
    'PROFILE_DOCX',
    'PROFILE_JSON',
    'JD_PDF',
    'JD_DOCX',
    'JD_JSON',
    'OPEN_JD_COPY',
    'OTHER',
  ] as const,
  {
    CV_ORIGINAL: 'Original CV',
    PROFILE_PDF: 'Profile PDF',
    PROFILE_DOCX: 'Profile DOCX',
    PROFILE_JSON: 'Profile JSON',
    JD_PDF: 'JD PDF',
    JD_DOCX: 'JD DOCX',
    JD_JSON: 'JD JSON',
    OPEN_JD_COPY: 'Open JD copy',
    OTHER: 'Other',
  },
  { cv: 'CV_ORIGINAL', 'cv original': 'CV_ORIGINAL', 'original cv': 'CV_ORIGINAL', 'jd pdf': 'JD_PDF' },
);
export type FileKind = (typeof FileKind.codes)[number];

export const UserRole = defineEnum(['ADMIN', 'RECRUITER', 'VIEWER'] as const, {
  ADMIN: 'Admin',
  RECRUITER: 'Recruiter',
  VIEWER: 'Viewer',
});
export type UserRole = (typeof UserRole.codes)[number];

export const ApiScope = defineEnum(['READ', 'WRITE', 'DOCUMENTS', 'IMPORT', 'ADMIN'] as const, {
  READ: 'read',
  WRITE: 'write',
  DOCUMENTS: 'documents',
  IMPORT: 'import',
  ADMIN: 'admin',
});
export type ApiScope = (typeof ApiScope.codes)[number];

export const ViewLayout = defineEnum(['TABLE', 'BOARD'] as const, { TABLE: 'Table', BOARD: 'Board' });
export type ViewLayout = (typeof ViewLayout.codes)[number];

export const MarginLevel = defineEnum(['GLOBAL', 'POSITION', 'SUBMISSION'] as const, {
  GLOBAL: 'global',
  POSITION: 'position',
  SUBMISSION: 'submission',
});
export type MarginLevel = (typeof MarginLevel.codes)[number];

/** CEFR levels plus the free-form values used for CVs. */
export const LANGUAGE_LEVELS = [
  'A1',
  'A2',
  'B1',
  'B2',
  'C1',
  'C2',
  'native',
  'beginner',
  'not stated',
] as const;
export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];

export function normalizeLanguageLevel(level: string | undefined | null): LanguageLevel {
  if (!level) return 'not stated';
  const t = level.trim();
  const upper = t.toUpperCase();
  if ((LANGUAGE_LEVELS as readonly string[]).includes(upper)) return upper as LanguageLevel;
  const n = normalizeToken(t);
  if (['native', 'mother_tongue', 'muttersprache', 'nativ', 'muttersprachler'].includes(n)) return 'native';
  if (['fluent', 'fliessend', 'verhandlungssicher', 'business_fluent', 'proficient'].includes(n)) return 'C1';
  if (['advanced', 'fortgeschritten', 'upper_intermediate', 'good', 'gut'].includes(n)) return 'B2';
  if (['intermediate', 'conversational', 'mittel', 'grundkenntnisse'].includes(n)) return 'B1';
  if (['basic', 'basics', 'elementary', 'beginner', 'anfaenger', 'anfanger', 'grundlagen'].includes(n))
    return 'beginner';
  if (['not_stated', 'unknown', 'n_a', 'na', 'none', 'unbekannt'].includes(n)) return 'not stated';
  const m = upper.match(/\b([ABC][12])\b/);
  if (m) return m[1] as LanguageLevel;
  return 'not stated';
}
